import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaExclamationTriangle, FaTimesCircle, FaPlus } from 'react-icons/fa';

const API_URL = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

/**
 * Compact token balance indicator for navigation/header
 * Shows current token count and status
 */
const TokenBalanceIndicator = ({ compact = false }) => {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const navigate = useNavigate();
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const fetchBalance = async () => {
            if (!isAuthenticated) return;
            
            try {
                const token = await getAccessTokenSilently();
                const response = await fetch(`${API_URL}/tokens/balance`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setBalance(data.data?.balance);
                }
            } catch (error) {
                console.error('Error fetching token balance:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBalance();
        const interval = setInterval(fetchBalance, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [getAccessTokenSilently, isAuthenticated]);

    if (!isAuthenticated || loading) {
        return null;
    }

    const getStatusInfo = () => {
        if (!balance || balance.totalTokens === 0) {
            return { color: '#ef4444', text: 'No tokens', bgColor: 'rgba(239, 68, 68, 0.2)' };
        }
        
        const percentage = (balance.availableTokens / balance.totalTokens) * 100;
        
        if (balance.isExpired) {
            return { color: '#ef4444', text: 'Expired', bgColor: 'rgba(239, 68, 68, 0.2)' };
        }
        if (percentage <= 5) {
            return { color: '#ef4444', text: 'Critical', bgColor: 'rgba(239, 68, 68, 0.2)' };
        }
        if (percentage <= 20) {
            return { color: '#f59e0b', text: 'Low', bgColor: 'rgba(245, 158, 11, 0.2)' };
        }
        return { color: '#22c55e', text: 'Good', bgColor: 'rgba(34, 197, 94, 0.2)' };
    };

    const status = getStatusInfo();
    const percentage = balance?.totalTokens > 0 
        ? Math.round((balance.availableTokens / balance.totalTokens) * 100) 
        : 0;

    const goToSettings = () => {
        setMenuOpen(false);
        navigate('/settings/tokens');
    };

    const formatTokens = (count) => {
        if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
        if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
        return count?.toString() || '0';
    };

    const styles = {
        wrapper: {
            position: 'relative'
        },
        indicator: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: 'rgba(91, 139, 208, 0.1)',
            border: '1px solid rgba(91, 139, 208, 0.2)',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
        },
        indicatorHover: {
            backgroundColor: 'rgba(91, 139, 208, 0.2)'
        },
        icon: {
            fontSize: '16px',
            color: status.color
        },
        value: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#fff'
        },
        dot: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: status.color
        },
        menu: {
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '280px',
            backgroundColor: '#1a1f26',
            border: '1px solid rgba(100, 150, 200, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            zIndex: 1000,
            overflow: 'hidden'
        },
        menuHeader: {
            padding: '16px',
            borderBottom: '1px solid rgba(100, 150, 200, 0.2)'
        },
        menuLabel: {
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '4px'
        },
        menuValue: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#fff'
        },
        progressBar: {
            height: '6px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
            marginTop: '12px',
            overflow: 'hidden'
        },
        progressFill: {
            height: '100%',
            backgroundColor: status.color,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
        },
        progressInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '8px'
        },
        progressText: {
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.6)'
        },
        statusBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: status.bgColor,
            color: status.color
        },
        expiry: {
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.5)',
            marginTop: '8px'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            borderTop: '1px solid rgba(100, 150, 200, 0.1)'
        },
        addTokensSection: {
            padding: '16px',
            borderTop: '1px solid rgba(100, 150, 200, 0.2)',
            backgroundColor: 'rgba(91, 139, 208, 0.05)'
        },
        addBtn: {
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#5B8BD0',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        promoHint: {
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.5)',
            textAlign: 'center',
            marginTop: '8px'
        }
    };

    if (compact) {
        return (
            <div 
                style={styles.indicator} 
                onClick={goToSettings}
                title={`${balance?.availableTokens?.toLocaleString() || 0} tokens available`}
            >
                <FaCoins style={styles.icon} />
                <div style={styles.dot} />
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            <div 
                style={styles.indicator} 
                onClick={() => setMenuOpen(!menuOpen)}
            >
                <FaCoins style={styles.icon} />
                <span style={styles.value}>{formatTokens(balance?.availableTokens)}</span>
                {(status.color === '#ef4444' || status.color === '#f59e0b') && (
                    <div style={styles.dot} />
                )}
            </div>

            {menuOpen && (
                <>
                    <div 
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                        onClick={() => setMenuOpen(false)}
                    />
                    <div style={styles.menu}>
                        <div style={styles.menuHeader}>
                            <div style={styles.menuLabel}>Token Balance</div>
                            <div style={styles.menuValue}>
                                {balance?.availableTokens?.toLocaleString() || 0}
                            </div>
                            
                            <div style={styles.progressBar}>
                                <div style={{ ...styles.progressFill, width: `${percentage}%` }} />
                            </div>
                            
                            <div style={styles.progressInfo}>
                                <span style={styles.progressText}>{percentage}% remaining</span>
                                <span style={styles.statusBadge}>{status.text}</span>
                            </div>

                            {balance?.daysUntilExpiry && (
                                <div style={styles.expiry}>
                                    Expires in {balance.daysUntilExpiry} days
                                </div>
                            )}
                        </div>

                        <div style={styles.menuItem} onClick={goToSettings}>
                            <FaCoins /> Manage Tokens
                        </div>

                        <div style={styles.addTokensSection}>
                            <button style={styles.addBtn} onClick={goToSettings}>
                                <FaPlus /> Add Tokens
                            </button>
                            {(status.color === '#ef4444' || status.color === '#f59e0b') && (
                                <div style={styles.promoHint}>
                                    Use code <strong>iMavenAi</strong> for 100% off!
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default TokenBalanceIndicator;
